// Citations arrive via two different paths that must normalize to the same shape:
// - live streaming: attached directly as `message.citations` by useChat's DONE handler
// - reloaded history: serialized by the backend into `message.metadata` as a
//   `{meta_key: "citations", meta_value: "<json string>"}` entry (chat/serializer.py)
export const getMessageCitations = (message) => {
    if (Array.isArray(message?.citations)) {
        return message.citations;
    }

    if (Array.isArray(message?.metadata)) {
        const citationsMeta = message.metadata.find(meta => meta.meta_key === 'citations');
        if (citationsMeta?.meta_value) {
            try {
                const parsed = JSON.parse(citationsMeta.meta_value);
                return Array.isArray(parsed) ? parsed : [];
            } catch (error) {
                console.error('Failed to parse citations metadata:', error);
            }
        }
    }

    return [];
};

// The model only ever emits a lightweight cite:N marker as the href - never a
// real URL (see rag/chat.py's prompt) - so the real destination is resolved here
// from the citations array, not trusted from the model's output. This must run
// BEFORE DOMPurify.sanitize: DOMPurify's default config strips href attributes
// with an unrecognised scheme like "cite:", silently leaving the link text inert.
const CITE_HREF_RE = /href=(["'])cite:(\d+)\1/g;

// The prompt instructs the model to only link words that were already part of
// a sentence, but it sometimes ignores that and bolts a citation onto the end
// of a sentence that has already finished - a restated fact/title whose only
// purpose is to hold a link, e.g. "...on 30 June 2023. <a href="cite:19">accounts
// for the year ended 31 March 2023</a></p>". That shape is detectable
// mechanically: a citation link sitting right after a sentence-ending period,
// with nothing else before the paragraph/list-item/heading closes. Genuine
// inline citations always have real sentence text on both sides, so this
// pattern only ever matches the bolted-on case.
//
// Dropping it outright (an earlier version of this) turned out too aggressive -
// the model uses this shape often enough, especially in bullet lists, that whole
// answers were losing most or all of their citations. Instead, convert it into a
// small superscript footnote marker (the same convention Wikipedia/academic
// citations use for sentence-level attribution) - it stays clickable via the same
// data-citation-index handling as inline links, it just isn't pretending to be a
// quoted phrase anymore.
const TRAILING_CITATION_FRAGMENT_RE =
    /\.\s*<a\s+href=(["'])cite:(\d+)\1\s*>[^<]*<\/a>\s*(?=<\/(?:p|li|h[1-6])>|$)/gi;

const toCitationMarker = (_match, _quote, citationIndex) =>
    `.<sup class="citation-marker" data-citation-index="${citationIndex}">${citationIndex}</sup>`;

const convertTrailingCitationFragments = (html) =>
    html.replace(TRAILING_CITATION_FRAGMENT_RE, toCitationMarker);

export const resolveCitationLinks = (html) =>
    convertTrailingCitationFragments(html).replace(CITE_HREF_RE, (_match, _quote, citationIndex) =>
        `href="#" class="citation-link" data-citation-index="${citationIndex}"`
    );

// NHS board-pack filenames conventionally already end in their meeting month
// and year (e.g. "...Board Papers September 2024.pdf") - that's unrelated to,
// but usually the same value as, the separately-tagged publish_year metadata.
// Appending publish_year unconditionally would show that year twice.
const yearAlreadyInSource = (source, year) =>
    Boolean(source && year && String(source).includes(String(year)));

// Shared by the inline citation tooltip and the PDF viewer header (see
// ChatMessageDisplay.js's offcanvas) so both surfaces describe a source the
// same way: "<doc name> · <year>", skipping the year when the filename
// already shows it, and skipping either part when absent.
const formatCitationSourceYear = (citation) => {
    const source = citation?.source;
    const year = citation?.publish_year;
    const showYear = year && !yearAlreadyInSource(source, year);
    return [source, showYear ? year : null].filter(Boolean).join(' · ');
};

// The PDF viewer header (ChatMessageDisplay.js's offcanvas) shows the filename
// itself as its heading, then this as the subtitle: publish year and document
// category (e.g. "2024 · Board Papers") - skipping the year when the filename
// (the heading) already ends in it, so it isn't shown twice.
export const citationHeaderSubtitle = (citation) => {
    if (!citation) return '';

    const year = citation.publish_year;
    const showYear = year && !yearAlreadyInSource(citation.source, year);
    return [showYear ? year : null, citation.document_category].filter(Boolean).join(' | ');
};

const escapeAttribute = (value) =>
    String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

// Runs after resolveCitationLinks, once the html already carries
// data-citation-index markers on both inline links and footnote markers - adds
// a native title attribute so hovering a citation shows the source doc name
// and year without needing to open the PDF viewer.
const CITATION_INDEX_ATTR_RE = /data-citation-index="(\d+)"/g;

export const addCitationTooltips = (html, citations) =>
    html.replace(CITATION_INDEX_ATTR_RE, (match, indexStr) => {
        const citation = citations.find((c) => c.index === Number(indexStr));
        const label = formatCitationSourceYear(citation);
        if (!label) return match;

        return `${match} title="${escapeAttribute(label)}"`;
    });
